import { IconInputProps, IconV2 } from '@kizi/shared-components';
import nl from 'date-fns/locale/nl';
import React, { useMemo, useRef } from 'react';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useFieldTracking } from '../../../../utils/hooks';
import { withFormProps, WithFormProps } from '../../../HOCs';
import { DatePickerWrapper } from './DatePickerWrapper';

interface ReactDatePickerInputProps
  extends Omit<IconInputProps, 'iconProps' | 'size'> {
  dateFormat?: string;
  placeholder?: string;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
  timeCaption?: string;
  minDate?: Date;
  maxDate?: Date;
  excludeDates?: Date[];
  includeDates?: Date[];
  filterDate?: (date: Date) => boolean;
  showWeekNumbers?: boolean;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  dropdownMode?: 'scroll' | 'select';
  yearDropdownItemNumber?: number;
  scrollableYearDropdown?: boolean;
  isClearable?: boolean;
  calendarStartDay?: number;
  locale?: any;
  peekNextMonth?: boolean;
  showMonthYearPicker?: boolean;
  showQuarterYearPicker?: boolean;
  showYearPicker?: boolean;
  inline?: boolean;
  fixedHeight?: boolean;
  monthsShown?: number;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date;
  endDate?: Date;
  excludeScrollbar?: boolean;
  customInput?: React.ReactElement;
  onCalendarClose?: () => void;
  onCalendarOpen?: () => void;
  calendarIcon?: React.ReactNode;
  showCalendar?: boolean;
  validators?: any;
  valueType?: string;
  meta?: {
    originalQuestion?: {
      valueType?: string;
      validators?: any[];
      [key: string]: any;
    };
    [key: string]: any;
  };
}

registerLocale('nl', nl);
setDefaultLocale('nl');

const ReactDatePickerInputBase: React.FC<
  WithFormProps<ReactDatePickerInputProps>
> = ({
  name,
  value,
  onChange,
  placeholder = 'DD-MM-YYYY',
  dateFormat = 'dd-MM-yyyy',
  showTimeSelect = false,
  showTimeSelectOnly = false,
  timeIntervals = 15,
  timeCaption = 'Time',
  minDate,
  maxDate,
  excludeDates,
  includeDates,
  filterDate,
  showWeekNumbers = false,
  showMonthDropdown = false,
  showYearDropdown = true,
  dropdownMode = 'scroll',
  yearDropdownItemNumber = 80,
  scrollableYearDropdown = true,
  isClearable = false,
  calendarStartDay = 1,
  locale = 'nl',
  peekNextMonth = true,
  showMonthYearPicker = false,
  showQuarterYearPicker = false,
  showYearPicker = false,
  inline = false,
  fixedHeight = false,
  monthsShown = 1,
  selectsStart = false,
  selectsEnd = false,
  startDate,
  endDate,
  excludeScrollbar = false,
  customInput,
  onCalendarClose,
  onCalendarOpen,
  calendarIcon,
  disabled,
  readOnly,
  _error,
  _errorProps,
  _successProps,
  shouldTriggerAPICall,
  showCalendar = false,
  validators,
  valueType,
  meta,
  ...props
}) => {
  const { enhanceInputProps } = useFieldTracking();
  const datePickerRef = useRef<DatePicker>(null);

  const shouldShowCalendar = useMemo(() => {
    // Check if valueType is date or if meta indicates it's a date field
    if (valueType === 'date' || meta?.originalQuestion?.valueType === 'date') {
      return true;
    }

    return showCalendar;
  }, [showCalendar, name, valueType, meta]);

  const dateConstraints = useMemo(() => {
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    // Use validators from props or meta
    const validatorList = validators || meta?.originalQuestion?.validators;

    if (validatorList && Array.isArray(validatorList)) {
      validatorList.forEach((validator) => {
        console.log('DateInput Debug - Processing validator:', validator);

        // Check for "TodayOrGreater" validator (from your example)
        if (validator.name === 'TodayOrGreater') {
          minDate = new Date();
        }

        // Check for other common validators
        if (validator.name === 'afterToday') {
          minDate = new Date();
        }

        if (validator.name === 'beforeToday') {
          maxDate = new Date();
        }

        // Check for future date limits
        if (
          validator.name === 'exceedsFuture' &&
          validator.args &&
          validator.args.length > 0
        ) {
          const daysInFuture = parseInt(validator.args[0]) || 90;
          const futureDate = new Date();
          futureDate.setDate(futureDate.getDate() + daysInFuture);
          maxDate = futureDate;
        }
      });
    }

    return { minDate, maxDate };
  }, [validators, meta]);

  const hasError = Boolean(_error);
  const hasSuccess = Boolean(!hasError && value && !disabled);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Format date to dd-MM-yyyy format (e.g., "12-08-1991")
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString();
      const formattedDate = `${day}-${month}-${year}`;

      // Create synthetic event to match expected signature
      const syntheticEvent = {
        target: { name: name || '', value: formattedDate },
        currentTarget: { name: name || '', value: formattedDate },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    } else {
      const syntheticEvent = {
        target: { name: name || '', value: '' },
        currentTarget: { name: name || '', value: '' },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    }
  };

  const enhancedProps = enhanceInputProps({ name, ...props });

  return (
    <DatePickerWrapper hasError={hasError} hasSuccess={hasSuccess}>
      <DatePicker
        {...enhancedProps}
        ref={datePickerRef}
        onChange={() => {}}
        onSelect={handleDateChange}
        onChangeRaw={onChange}
        value={value}
        onCalendarClose={onCalendarClose}
        onCalendarOpen={onCalendarOpen}
        placeholderText={placeholder}
        dateFormat={dateFormat}
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        timeIntervals={timeIntervals}
        timeCaption={timeCaption}
        minDate={dateConstraints.minDate || minDate}
        maxDate={dateConstraints.maxDate || maxDate}
        excludeDates={excludeDates}
        includeDates={includeDates}
        filterDate={filterDate}
        showWeekNumbers={showWeekNumbers}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        dropdownMode={dropdownMode}
        yearDropdownItemNumber={yearDropdownItemNumber}
        scrollableYearDropdown={scrollableYearDropdown}
        isClearable={isClearable}
        calendarStartDay={calendarStartDay}
        locale={locale}
        peekNextMonth={peekNextMonth}
        showMonthYearPicker={showMonthYearPicker}
        showQuarterYearPicker={showQuarterYearPicker}
        showYearPicker={showYearPicker}
        inline={inline}
        fixedHeight={fixedHeight}
        monthsShown={monthsShown}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        startDate={startDate}
        endDate={endDate}
        excludeScrollbar={excludeScrollbar}
        customInput={customInput}
        disabled={disabled}
        readOnly={readOnly}
        data-testid={name ? `datepicker-${name}` : undefined}
        autoComplete="off"
        showPopperArrow={false}
        open={shouldShowCalendar ? undefined : false}
        popperModifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
        ]}
      />
      {calendarIcon && (
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {calendarIcon}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconV2 name="calendar" width={18} height={18} fill={'#9B9B9B'} />
      </div>
    </DatePickerWrapper>
  );
};

const ReactDatePickerInputWithFormProps = withFormProps(
  ReactDatePickerInputBase,
);

export const ReactDatePickerInput: React.FC<
  WithFormProps<ReactDatePickerInputProps>
> = (props) => (
  <ReactDatePickerInputWithFormProps
    {...props}
    _errorProps={{
      variant: 'error',
      _labelProps: { variant: 'error' },
      ...props._errorProps,
    }}
    _successProps={{
      variant: 'success',
      ...props._successProps,
    }}
  />
);

export type { ReactDatePickerInputProps };
